import * as T from 'three';

/** Retain the middle depth partition before the next slice clears it. Only
 * active for local volcanic volumes; one fullscreen copy, no geometry redraw. */
export class DistantSceneDepth {
 readonly uniforms={uDistantDepth:{value:null as T.Texture|null},uDistantNear:{value:600},uDistantOn:{value:0}};
 private target:T.WebGLRenderTarget|null=null;
 private readonly scene=new T.Scene();
 private readonly camera=new T.Camera();
 private readonly material=new T.ShaderMaterial({
  depthTest:false,depthWrite:false,toneMapped:false,
  uniforms:{uSource:{value:null as T.Texture|null},uRange:{value:new T.Vector2()},uTexel:{value:new T.Vector2()}},
  vertexShader:'varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position.xy,0.0,1.0);}',
  fragmentShader:'varying vec2 vUv;uniform sampler2D uSource;uniform vec2 uRange,uTexel;void main(){vec2 t=uTexel*.5;float d=min(min(texture2D(uSource,vUv-t).r,texture2D(uSource,vUv+t).r),min(texture2D(uSource,vUv+vec2(t.x,-t.y)).r,texture2D(uSource,vUv+vec2(-t.x,t.y)).r));float z=uRange.x*uRange.y/(uRange.y-d*(uRange.y-uRange.x));gl_FragColor=vec4(z,0.0,0.0,1.0);}',
 });
 private readonly quad=new T.Mesh(new T.PlaneGeometry(2,2),this.material);
 constructor(){this.quad.frustumCulled=false;this.scene.add(this.quad);}
 beginFrame():void{this.uniforms.uDistantOn.value=0;}
 capture(renderer:T.WebGLRenderer,near:number,far:number):void {
  // Local planet landforms fit in [600,300000]. The nearest slice survives
  // normally; the orbital slice cannot occlude a local vent.
  if(near<599||near>=300000)return;
  const source=renderer.getRenderTarget();
  if(!source?.depthTexture||!renderer.extensions.has('EXT_color_buffer_float'))return;
  // Retain the closest of each 2×2 source block: half-size storage, conservative
  // silhouettes, and no missed thin occluder between the volume's coarse taps.
  const width=Math.ceil(source.width/2),height=Math.ceil(source.height/2);
  if(!this.target||this.target.width!==width||this.target.height!==height){
   this.target?.dispose();this.target=new T.WebGLRenderTarget(width,height,{format:T.RedFormat,type:T.FloatType,depthBuffer:false,minFilter:T.NearestFilter,magFilter:T.NearestFilter});
  }
  this.material.uniforms.uSource.value=source.depthTexture;this.material.uniforms.uRange.value.set(near,far);
  this.material.uniforms.uTexel.value.set(1/source.width,1/source.height);
  const auto=renderer.autoClear;renderer.autoClear=true;renderer.setRenderTarget(this.target);renderer.render(this.scene,this.camera);renderer.setRenderTarget(source);renderer.autoClear=auto;
  this.uniforms.uDistantDepth.value=this.target.texture;this.uniforms.uDistantNear.value=near;this.uniforms.uDistantOn.value=1;
 }
 dispose():void{this.target?.dispose();this.target=null;this.quad.geometry.dispose();this.material.dispose();this.beginFrame();}
}
export const DISTANT_DEPTH_GLSL=/* glsl */`
uniform sampler2D uDistantDepth;
uniform float uDistantNear,uDistantOn;
float completeViewDepth(vec2 uv,float nearDepth){
 if(uDistantOn>.5&&nearDepth>=uDistantNear*.99999)return texture2D(uDistantDepth,uv).r;
 return nearDepth;
}
`;
